import React from 'react';
import { useTranslation } from 'react-i18next';

import { UnlockCallout } from './design-system/plan/UnlockCallout';
import { useVendorPlan } from '../common/hooks/use-vendor-plan.hook';
import { PlanFeatures } from '../modules/superadmin/our-vendors/models/vendor.model';

interface FeatureGateProps {
  feature: keyof PlanFeatures;
  title?: string;
  description?: string;
  children: React.ReactNode;
}

/**
 * Wraps a region (typically a whole page body) whose feature a plan may not
 * include.
 *
 * It no longer hides the page. Every feature is open on the Free plan (the
 * API sets the flags true), so for most vendors this renders its children and
 * nothing else. When a flag really is off — Ads on Free, which needs a
 * reviewed Meta connection — the page still renders, with an unlock card
 * above it: the vendor sees what is there, and the one thing the card says
 * is what Pro adds. The API still refuses what the plan does not include.
 */
export const FeatureGate: React.FC<FeatureGateProps> = ({ feature, title, description, children }) => {
  const { hasFeature } = useVendorPlan();
  const { t } = useTranslation();

  if (hasFeature(feature)) return <>{children}</>;

  const generic = (part: 'TITLE' | 'BODY') => t(`FREE_PLAN.FEATURE.GENERIC.${part}`);
  const why = t(`FREE_PLAN.FEATURE.${feature}.WHY`, { defaultValue: '' });

  return (
    <div data-testid={`feature-locked-${feature}`} className="flex flex-col gap-6">
      <UnlockCallout
        testId={`feature-callout-${feature}`}
        reason={feature}
        title={title || t(`FREE_PLAN.FEATURE.${feature}.TITLE`, { defaultValue: generic('TITLE') })}
        body={description || t(`FREE_PLAN.FEATURE.${feature}.BODY`, { defaultValue: generic('BODY') })}
        why={why || undefined}
      />
      {children}
    </div>
  );
};

export default FeatureGate;
